/**
 * Utility functions for analyzing slate image dimensions
 */

export type ImageDimension = {
  width: number;
  height: number;
};

// Constants for elongated image detection
const ELONGATED_IMAGE_RATIO = 0.625; // 1200x1920 aspect ratio
const VARIANCE_THRESHOLD = 0.01;

/**
 * Checks if an image is elongated based on the specific ratio and variance threshold
 */
export const checkImageElongated = (width: number, height: number): boolean => {
  const ratio = width / height;
  return Math.abs(ratio - ELONGATED_IMAGE_RATIO) < VARIANCE_THRESHOLD;
};

/**
 * Analyzes an image URL and returns its dimensions
 */
export const getImageDimensions = (url: string): Promise<ImageDimension> =>
  new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        height: img.naturalHeight,
        width: img.naturalWidth,
      });
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${url}`));
    };
    img.src = url;
  });

/**
 * Analyzes multiple image URLs and returns their dimensions
 */
export const analyzeSlateImageDimensions = async (slateUrls: string[]): Promise<ImageDimension[]> => {
  const dimensionPromises = slateUrls.map(async (url) => {
    try {
      return await getImageDimensions(url);
    } catch (error) {
      console.warn(`Failed to analyze dimensions for ${url}:`, error);
      // Return square (default) for failed images
      return {
        height: 500,
        width: 500,
      };
    }
  });

  return Promise.all(dimensionPromises);
};

/**
 * Extracts unique image URLs from slate list for dimension analysis
 */
export const extractSlateImageUrls = (
  slatesList: Array<{
    url: string;
    removed: boolean;
  }>,
): string[] => {
  return slatesList
    .filter((slate) => !slate.removed && slate.url)
    .map((slate) => slate.url)
    .filter((url, index, array) => array.indexOf(url) === index); // Remove duplicates
};

/**
 * Creates ImageDimensionInput array for GraphQL mutation
 */
export const createImageDimensionInputs = (
  dimensions: ImageDimension[],
): Array<{
  width: number;
  height: number;
}> =>
  dimensions.map((dim) => ({
    height: dim.height,
    width: dim.width,
  }));
